<template>
  <Card>
    <h2 slot="header">Payment</h2>
    <div class="has-text-centered">
      <Table>
        <tbody>
          <tr>
            <td colspan="2">Camping</td>
            <td class="text-xs-right">${{ campsiteTotals.retail.toFixed(2) }}</td>
          </tr>
          <tr>
            <td colspan="2">Parking</td>
            <td class="text-xs-right">${{ parkingTotals.retail.toFixed(2) }}</td>
          </tr>
          <tr>
            <td colspan="2">GatePasses</td>
            <td class="text-xs-right">${{ gatePassTotals.retail.toFixed(2) }}</td>
          </tr>
          <tr>
            <td colspan="2">Discount</td>
            <td class="text-xs-right">{{ workingInvoice.discount }}%</td>
          </tr>
          <tr>
            <td colspan="2">SubTotal</td>
            <td class="text-xs-right">${{ subTotal.toFixed(2) }}</td>
          </tr>
          <tr>
            <td colspan="2">Additional Fees</td>
            <td class="text-xs-right">${{ workingInvoice.fees }}</td>
          </tr>
          <tr>
            <td colspan="2">Grand Total</td>
            <td class="text-xs-right">${{ grandTotal.toFixed(2) }}</td>
          </tr>
          <tr v-for="payment in payments">
            <td>
              {{ payment.paymentTypeID }}<br>
              {{ payment.confirmation }}
            </td>
            <td>
              <IconButton
                icon="fa-undo"
                tooltip="undo"
                @click="deletePayment(payment).then(() => getInvoicePayments(workingInvoice))"
              />
            </td>
            <td class="text-xs-right">${{ payment.amount }}</td>
          </tr>
        </tbody>
      </Table>
      <!-- finalize button -->
      <template v-if="settings.personReserve == 1 && paymentsMatchTotal">
        <v-btn
          color="red"
          dark
          @click="$router.go(-1)"
        >
          Back
        </v-btn>
        <v-btn
          @click="finalizeInvoice()"
        >
          Finalize
        </v-btn>
      </template>
      <Multitender
        v-else-if="settings.personReserve == 1"
        :key="multitenderRenderTick"
        :balance="remainingBalance"
        :is-processing="isProcessing"
        @submit="submitPayment"
      />
      <Single
        v-else
        :amount="remainingBalance"
        :is-processing="isProcessing"
        @submit="submitPayment"
      />
    </div>
  </Card>
</template>

<script>
import checkoutMixin from '@/mixins/checkout'
import Single from './paymentForms/Single'
import Multitender from './paymentForms/Multitender'
// Import vuex map
import { mapGetters, mapActions, mapMutations } from 'vuex'
// import config for credit processing
import config from '@/config.js'

export default {
  components: {
    Single,
    Multitender
  },
  mixins: [
    checkoutMixin.totals
  ],
  data () {
    return {
      isProcessing: false,
      paymentData: null,
      multitenderRenderTick: false
    }
  },
  computed: {
    ...mapGetters({
      settings: 'person/settings',
      payments: 'invoices/payments',
      workingInvoice: 'invoices/workingInvoice'
    }),
    paymentTotal () {
      let paymentTotal = this.payments.reduce((total, payment) => {
        return total + Number(payment.amount)
      }, 0)
      return paymentTotal
    },
    remainingBalance () {
      return this.grandTotal - this.paymentTotal
    },
    paymentsMatchTotal () {
      return this.paymentTotal === this.grandTotal
    }
  },
  mounted () {
    // load accept script
    let AcceptScript = document.createElement('script')
    AcceptScript.setAttribute('src', config.authorizeScript)
    document.head.appendChild(AcceptScript)
    this.clearStalePayments()
    this.getWorkingInvoice().then(() => {
      this.getInvoicePayments(this.workingInvoice)
    })
    this.getCart()
  },
  methods: {
    ...mapMutations({
      alert: 'info/newAlert'
    }),
    ...mapActions({
      getWorkingInvoice: 'invoices/getWorkingInvoice',
      createPayment: 'invoices/createPayment',
      deletePayment: 'invoices/deletePayment',
      getInvoicePayments: 'invoices/getInvoicePayments',
      clearStalePayments: 'invoices/clearStalePayments',
      getCart: 'cart/getCart',
      $finalizeInvoice: 'invoices/finalizeInvoice'
    }),
    submitPayment (data) {
      // update the button state
      this.isProcessing = true
      this.paymentData = data
      if (data.paymentTypeID === 'creditCard') {
        this.getAuthorizeNonce(data)
      } else {
        this.sendPaymentToApi(data)
      }
    },
    getAuthorizeNonce (cardData) {
      // format data to dispatch to authorize
      var secureData = {
        authData: {
          clientKey: config.authorizeCredentials.clientKey,
          apiLoginID: config.authorizeCredentials.loginId
        },
        cardData
      }
      Accept.dispatchData(secureData, this.handleAuthorizeResponse) // eslint-disable-line no-undef
    },
    handleAuthorizeResponse (response) {
      if (response.messages.resultCode === 'Error') {
        this.handleAuthorizeErrors(response.messages.message)
      } else {
        let payment = {
          paymentTypeID: this.paymentData.paymentTypeID,
          firstName: this.paymentData.firstName,
          lastName: this.paymentData.lastName,
          address: this.paymentData.address,
          city: this.paymentData.city,
          state: this.paymentData.state,
          zip: this.paymentData.zip,
          country: this.paymentData.country,
          amount: this.paymentData.amount,
          opaqueData: response.opaqueData
        }
        this.sendPaymentToApi(payment)
      }
    },
    handleAuthorizeErrors (messages) {
      Object.entries(messages).forEach(([key, message]) => {
        let entry = {
          title: 'Credit Error: ' + message.code,
          message: message.text,
          request: 'authorize.net: accept.js'
        }
        this.alert(entry)
        this.isProcessing = false
      })
    },
    sendPaymentToApi (data) {
      this.createPayment(data).then(() => {
        this.haltProcessing()
        this.multitenderRenderTick = !this.multitenderRenderTick // resets the payment form
        if (this.settings.personReserve === '0') {
          this.redirect()
        } else {
          this.paymentData = null
        }
      }, () => {
        this.haltProcessing()
      })
    },
    haltProcessing () {
      this.isProcessing = false
      this.getInvoicePayments(this.workingInvoice)
    },
    finalizeInvoice () {
      this.$finalizeInvoice().then(() => this.redirect())
    },
    redirect () {
      this.getCart()
      this.$router.push('/reservations')
    }
  }
}
</script>
